/**
 * D414: the guest reminder. The install prompt's card for a guest: add a passkey or an email so
 * the worlds outlive this browser. Never shown to a signed-in player, nor on a visit that shows
 * the install prompt. Dismissing hides it for the same week; settings' "Remind me to secure my
 * account" (`SecureSetting`) turns it off.
 */
import { openSignIn } from '../game/signin-bus.ts';
import { useAccount } from '../screens/SignInScreen.tsx';
import { Button } from '../ui/Button.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Switch } from './Switch.tsx';
import {
  claimPromptSlot,
  dismissSecurePrompt,
  useSecureRemind,
  useSecureSnoozedUntil,
} from './account-prompt.ts';
import { useIdle } from './store.ts';

export function SecurePrompt() {
  const now = useIdle((s) => s.now);
  const [remind] = useSecureRemind();
  const snoozedUntil = useSecureSnoozedUntil();
  const { account } = useAccount();
  if (!remind || now < snoozedUntil || !account || !account.isGuest) return null;
  if (!claimPromptSlot('secure')) return null;
  return (
    <div
      data-testid="secure-prompt"
      role="status"
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--line)',
        font: '400 13px/1.3 var(--font-ui)',
        flex: 'none',
      }}
    >
      <Icon name="user" size={18} />
      <span style={{ flex: 1 }}>Add a passkey or email to keep your worlds safe.</span>
      <Button variant="primary" data-testid="secure-prompt-action" onClick={openSignIn}>
        Set up
      </Button>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="secure-prompt-dismiss"
        onClick={() => dismissSecurePrompt(now)}
        style={{
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
          width: 44,
          height: 44,
          margin: '-6px -8px -6px 0',
          border: 'none',
          background: 'transparent',
          color: 'var(--ink-2)',
        }}
      >
        <Icon name="x" size={18} />
      </button>
    </div>
  );
}

/**
 * The settings row: "Remind me to secure my account", on by default (D414). D458: hidden once
 * the account has a passkey or an email; the secured state shows on the Signed in row.
 */
export function SecureSetting() {
  const [remind, setRemind] = useSecureRemind();
  const { account } = useAccount();
  if (account && !account.isGuest) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ font: '600 var(--fs-name)/1.2 var(--font-ui)' }}>
        Remind me to secure my account
      </span>
      <Switch label="Remind me to secure my account" on={remind} onChange={setRemind} />
    </div>
  );
}
